import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CreateDocumentResponse,
  GetDocumentDownloadUrlResponse,
  GetInvestorConfigResponse,
  GetInvestorResponse,
  GetStartupConfigResponse,
  V2AcceptTermsResponse,
  V2CreatePitchResponse,
  V2GetIntercomTokenResponse,
  V2GetInvestorPersonaResponse,
  V2GetInvestorProfileResponse,
  V2GetMeResponse,
  V2GetMyMembershipsResponse,
  V2GetPrescreenAllowanceResponse,
  V2GetPublicInvestorBySlugResponse,
  V2GetSignInProvidersResponse,
  V2GetStartupProfileResponse,
  V2ListAccessResponse,
  V2ListInvestorCreditsResponse,
  V2ListInvestorInvitesResponse,
  V2ListStartupCreditsResponse,
  V2ListStartupDocumentsResponse,
  V2ListStartupInvitesResponse,
  V2ListTeamMembersResponse,
  V2ProfileChatData,
  V2StartPitchResponse,
  V2UpdateMeResponse,
} from '@workspace/api-backend'
import {
  v2FounderInvitesPreview,
  v2ProfileChatContext,
  v2ProfileChatHistory,
  v2ProfileChatPreviewContext,
} from '@workspace/api-backend'
import { client } from '@workspace/api-backend/client'
import {
  createDocumentMutation,
  deleteDocumentMutation,
  getDocumentDownloadUrlMutation,
  getInvestorConfigOptions,
  getInvestorOptions,
  getStartupConfigOptions,
  v2AcceptInviteMutation,
  v2AcceptTermsMutation,
  v2AdminBackfillTeamMemberLinkedInMutation,
  v2AdminListInvestorDealFlowInteractionsOptions,
  v2AdminListInvestorDealFlowMatchesOptions,
  v2AdminListInvestorDealFlowMatchesQueryKey,
  v2AdminListStartupDealFlowInteractionsOptions,
  v2AdminRefreshInvestorDealFlowMatchesMutation,
  v2AdminSendInvestorDealFlowDigestMutation,
  v2AutoUnlockTopMatchMutation,
  v2CancelInvestorInviteMutation,
  v2CancelStartupInviteMutation,
  v2ConnectDealFlowMatchMutation,
  v2ContactFounderMutation,
  v2CreateAnalyticsExclusionMutation,
  v2CreateFeedbackMutation,
  v2CreateInvestorInviteMutation,
  v2CreatePitchMutation,
  v2CreateShareLinkMutation,
  v2CreateStartupInviteMutation,
  v2CreateTeamMemberMutation,
  v2CreateWalletTopUpCheckoutMutation,
  v2DeleteAnalyticsExclusionMutation,
  v2DeleteShareLinkMutation,
  v2DeleteTeamMemberMutation,
  v2DownloadDealFlowMatchDocumentMutation,
  v2EvaluateStartupMutation,
  v2FinishPitchMutation,
  v2FounderInvitesClaimMutation,
  v2FounderInvitesPreviewQueryKey,
  v2GenerateInvestorContactMessageMutation,
  v2GetActiveEntitlementsOptions,
  v2GetActiveEntitlementsQueryKey,
  v2GetAnalyticsSettingsOptions,
  v2GetAnalyticsSettingsQueryKey,
  v2GetBillingStatusOptions,
  v2GetBillingStatusQueryKey,
  v2GetDealFlowMatchOptions,
  v2GetDealFlowMatchVideoUrlMutation,
  v2GetDealFlowSubscriptionStatusOptions,
  v2GetDealFlowSubscriptionStatusQueryKey,
  v2GetInsightsReportOptions,
  v2GetIntercomTokenOptions,
  v2GetInvestorCreditBalanceOptions,
  v2GetInvestorEvaluationOptions,
  v2GetInvestorFeedOptions,
  v2GetInvestorJudgeProfileOptions,
  v2GetInvestorPersonaOptions,
  v2GetInvestorPersonaQueryKey,
  v2GetInvestorProfileOptions,
  v2GetInvestorProfileQueryKey,
  v2GetJudgeProfileOptions,
  v2GetMeOptions,
  v2GetMeQueryKey,
  v2GetMyMembershipsOptions,
  v2GetMyMembershipsQueryKey,
  v2GetPitchEvaluationOptions,
  v2GetPitchVideoUrlMutation,
  v2GetPrescreenAllowanceOptions,
  v2GetPrescreenAllowanceQueryKey,
  v2GetPublicInvestorBySlugOptions,
  v2GetPublicProfileFullOptions,
  v2GetPublicProfilePreviewOptions,
  v2GetSignInProvidersOptions,
  v2GetStartupAnalyticsOptions,
  v2GetStartupCreditBalanceOptions,
  v2GetStartupFeedOptions,
  v2GetStartupOutreachBodyOptions,
  v2GetStartupOutreachBodyQueryKey,
  v2GetStartupOverviewOptions,
  v2GetStartupOverviewQueryKey,
  v2GetStartupPitchOptions,
  v2GetStartupPitchQueryKey,
  v2GetStartupProfileOptions,
  v2GetStartupProfileQueryKey,
  v2GetStartupWalletOptions,
  v2GetStartupWalletQueryKey,
  v2GetStatementReactionsOptions,
  v2InvestorConnectWithStartupMutation,
  v2JoinAgentWaitlistMutation,
  v2LinkStartupDocumentMutation,
  v2ListAccessOptions,
  v2ListAccessQueryKey,
  v2ListDealFlowArchiveOptions,
  v2ListDealFlowMatchesOptions,
  v2ListDealFlowMatchesQueryKey,
  v2ListInvestorCreditsOptions,
  v2ListInvestorEvaluationRequestsOptions,
  v2ListInvestorInvitesOptions,
  v2ListInvestorInvitesQueryKey,
  v2ListShareLinksOptions,
  v2ListShareLinksQueryKey,
  v2ListStartupCreditsOptions,
  v2ListStartupDocumentsOptions,
  v2ListStartupInvitesOptions,
  v2ListStartupInvitesQueryKey,
  v2ListStartupMatchesOptions,
  v2ListStartupMatchesQueryKey,
  v2ListTeamMembersOptions,
  v2MarkPostImprovementPromptShownMutation,
  v2OpenWalletBillingPortalMutation,
  v2PassDealFlowMatchMutation,
  v2ProfileHeartbeatMutation,
  v2ProfileTrackEventMutation,
  v2PublicDocumentDownloadMutation,
  v2PublicVideoUrlMutation,
  v2RefreshTeamMemberLinkedInMutation,
  v2RegisterProfileVisitorMutation,
  v2RemoveAccessMutation,
  v2RequestInvestorEvaluationMutation,
  v2RequestStartupMatchEvaluationMutation,
  v2SetStartupStealthChoiceMutation,
  v2StartPitchMutation,
  v2SubmitInterestDecisionMutation,
  v2SubmitStatementReactionMutation,
  v2SubmitTextFeedbackMutation,
  v2SubscribeToDealFlowMutation,
  v2TriggerPrescreenMutation,
  v2UnlinkStartupDocumentMutation,
  v2UnsubscribeFromDealFlowMutation,
  v2UpdateInvestorMemberMutation,
  v2UpdateInvestorProfileMutation,
  v2UpdateMeMutation,
  v2UpdateShareLinkMutation,
  v2UpdateStartupDocumentMutation,
  v2UpdateStartupOutreachBodyMutation,
  v2UpdateStartupProfileMutation,
  v2UpdateTeamMemberMutation,
  verifyDocumentMutation,
} from '@workspace/api-backend/react-query'
import { trackEvent } from '../lib/gtag'

// ─── Re-export types used by consumers ───────────────────────────────────────

export type { StartupData, UserData } from '@workspace/api-backend'

import type { V2GetBillingStatusResponse } from '@workspace/api-backend'

export type BillingStatusData = NonNullable<V2GetBillingStatusResponse['data']>

// ─── User ────────────────────────────────────────────────────────────────────

export function useCurrentUserQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...v2GetMeOptions(),
    select: (response: V2GetMeResponse) => response.data,
    ...options,
  })
}

// ─── Sign-in providers (public, unauthenticated) ───────────────────────────

export function useSignInProvidersQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...v2GetSignInProvidersOptions(),
    select: (response: V2GetSignInProvidersResponse) => response.data,
    ...options,
  })
}

export function useIntercomTokenQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...v2GetIntercomTokenOptions(),
    select: (response: V2GetIntercomTokenResponse) => response.data.token,
    // The JWT is valid for 24h; refetch hourly so a long-lived session stays signed in.
    staleTime: 60 * 60 * 1000,
    ...options,
  })
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UpdateMeMutation(),
    onSuccess: (response: V2UpdateMeResponse) => {
      queryClient.setQueryData(v2GetMeQueryKey(), response)
    },
  })
}

export function useAcceptTermsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AcceptTermsMutation(),
    onSuccess: (response: V2AcceptTermsResponse) => {
      queryClient.setQueryData(v2GetMeQueryKey(), response)
    },
  })
}

// ─── Founder invites (public link, then join with one of the founder's startups) ──

/**
 * What the invite link shows. The raw call, not the query options, so a 404 (the one neutral answer for
 * an unknown, used or removed link) is told apart from a failure. The token is never logged.
 */
export function useFounderInvitePreviewQuery(token: string) {
  return useQuery({
    queryKey: v2FounderInvitesPreviewQueryKey({ path: { token } }),
    queryFn: async () => {
      const { data, response } = await v2FounderInvitesPreview({ path: { token } })
      if (response.status === 404) return 'not-found' as const
      if (!response.ok || !data) throw new Error('Founder invite preview failed')
      return data.data
    },
    retry: false,
    refetchOnWindowFocus: false,
  })
}

/** A founder joins through the invite with one of their startups. First claim wins. */
export function useClaimFounderInviteMutation() {
  return useMutation({
    ...v2FounderInvitesClaimMutation(),
  })
}

// ─── User's workspaces ──────────────────────────────────────────────────────

export function useMyMembershipsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...v2GetMyMembershipsOptions(),
    select: (response: V2GetMyMembershipsResponse) => response.data,
    ...options,
  })
}

// ─── Startup overview / mock endpoints ───────────────────────────────────────

export function useOverviewQuery(startupId: string) {
  return useQuery({
    ...v2GetStartupOverviewOptions({ path: { startupId } }),
    refetchInterval: (query) => {
      const data = query.state.data?.data
      if (data?.dashboardStatus === 'processing') return 5000
      if (data?.isReevaluating) return 5000
      if (data?.evaluationStatus === 'processing') return 10_000
      return false
    },
  })
}

export function useShareLinksQuery(startupId: string) {
  return useQuery(v2ListShareLinksOptions({ path: { startupId } }))
}

export function useCreateShareLinkMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2CreateShareLinkMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2ListShareLinksQueryKey({ path: { startupId } }) })
      trackEvent('dataroom_link_created', { startup_id: startupId })
    },
  })
}

export function useUpdateShareLinkMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UpdateShareLinkMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2ListShareLinksQueryKey({ path: { startupId } }) })
    },
  })
}

export function useDeleteShareLinkMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2DeleteShareLinkMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2ListShareLinksQueryKey({ path: { startupId } }) })
    },
  })
}

export function usePublicProfilePreviewQuery(token: string, enabled = true) {
  return useQuery({
    ...v2GetPublicProfilePreviewOptions({ path: { token } }),
    enabled: enabled && !!token,
  })
}

/**
 * The share link's full profile. An investor's page passes its `visitorToken`:
 * on a link that requires an email the server refuses content to a visitor
 * without one. The founder's own preview passes none and is let through on
 * their session.
 */
export function usePublicProfileFullQuery(
  token: string,
  visitorToken: string | undefined,
  queryOptions?: Partial<ReturnType<typeof v2GetPublicProfileFullOptions>>
) {
  return useQuery({
    ...v2GetPublicProfileFullOptions({
      path: { token },
      headers: visitorToken ? { 'x-visitor-token': visitorToken } : undefined,
    }),
    ...queryOptions,
  })
}

export function useRegisterProfileVisitorMutation() {
  return useMutation(v2RegisterProfileVisitorMutation())
}

/**
 * One pass through an email-capture door.
 *
 * Separate from `register` because the two carry different things: `register`
 * establishes identity, this records what the investor actually asked for. The
 * brief is the only field that says WHAT to build rather than how many want it.
 */
export function useJoinAgentWaitlistMutation() {
  return useMutation(v2JoinAgentWaitlistMutation())
}

export function useProfileHeartbeatMutation() {
  return useMutation(v2ProfileHeartbeatMutation())
}

export function useProfileTrackEventMutation() {
  return useMutation(v2ProfileTrackEventMutation())
}

export function usePublicDocumentDownloadMutation() {
  return useMutation(v2PublicDocumentDownloadMutation())
}

export function useContactFounderMutation() {
  return useMutation(v2ContactFounderMutation())
}

export function useSubmitReactionMutation() {
  return useMutation(v2SubmitStatementReactionMutation())
}

export function useSubmitTextFeedbackMutation() {
  return useMutation(v2SubmitTextFeedbackMutation())
}

export function useSubmitInterestDecisionMutation() {
  return useMutation(v2SubmitInterestDecisionMutation())
}

export function useReactionsQuery(token: string, visitorToken: string, enabled = true) {
  return useQuery({
    ...v2GetStatementReactionsOptions({
      path: { token },
      query: { visitorToken },
    }),
    enabled,
  })
}

export function useAnalyticsQuery(
  startupId: string,
  queryOptions?: Partial<ReturnType<typeof v2GetStartupAnalyticsOptions>>
) {
  return useQuery({
    ...v2GetStartupAnalyticsOptions({ path: { startupId } }),
    ...queryOptions,
  })
}

export function useInsightsReportQuery(startupId: string) {
  return useQuery(v2GetInsightsReportOptions({ path: { startupId } }))
}

export function usePitchQuery(
  startupId: string,
  queryOptions?: Partial<ReturnType<typeof v2GetStartupPitchOptions>>
) {
  return useQuery({
    ...v2GetStartupPitchOptions({ path: { startupId } }),
    ...queryOptions,
  })
}

export function useCreatePitchSessionMutation(callbacks?: {
  onSuccess?: (data: V2CreatePitchResponse) => void
  onError?: (error: Error) => void
}) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2CreatePitchMutation(),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: v2GetStartupPitchQueryKey({} as never) })
      callbacks?.onSuccess?.(data)
    },
    onError: (error) => callbacks?.onError?.(error),
  })
}

export function useStartPitchMutation(
  startupId: string,
  callbacks?: {
    onSuccess?: (data: V2StartPitchResponse) => void
    onError?: (error: Error) => void
  }
) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2StartPitchMutation(),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: v2GetStartupPitchQueryKey({} as never) }),
        queryClient.invalidateQueries({
          queryKey: v2GetBillingStatusQueryKey({ query: { startupId } }),
        }),
        queryClient.invalidateQueries({
          queryKey: v2GetStartupWalletQueryKey({ path: { id: startupId } }),
        }),
      ])
      callbacks?.onSuccess?.(data)
    },
    onError: (error) => callbacks?.onError?.(error),
  })
}

// ─── Startup matches (recommended investors) ────────────────────────────────

import { EVALUATION_REQUEST_STATUS, MATCHER_REFRESH_POLL_MS } from '../lib/matches'

export function useStartupMatchesQuery(startupId: string, limit?: number) {
  return useQuery({
    ...v2ListStartupMatchesOptions({
      path: { startupId },
      query: limit !== undefined ? { limit } : {},
    }),
    refetchInterval: (query) => {
      const matches = query.state.data?.data?.matches
      // Empty matches → matcher job hasn't completed yet (or failed). Cap the
      // poll: a permanently failed matcher would otherwise poll indefinitely.
      // ~5 minutes at 5s interval = 60 attempts before we give up.
      if (!matches || matches.length === 0) {
        return query.state.dataUpdateCount >= 60 ? false : MATCHER_REFRESH_POLL_MS
      }
      const hasInflight = matches.some(
        (m) =>
          m.evaluationRequestStatus === EVALUATION_REQUEST_STATUS.PENDING_PERSONA ||
          m.evaluationRequestStatus === EVALUATION_REQUEST_STATUS.PENDING_EVALUATION
      )
      return hasInflight ? MATCHER_REFRESH_POLL_MS : false
    },
  })
}

export function useRequestStartupMatchEvaluationMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2RequestStartupMatchEvaluationMutation(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: v2ListStartupMatchesQueryKey({ path: { startupId } }),
        }),
        queryClient.invalidateQueries({
          queryKey: v2GetBillingStatusQueryKey({ query: { startupId } }),
        }),
        queryClient.invalidateQueries({
          queryKey: v2GetStartupWalletQueryKey({ path: { id: startupId } }),
        }),
      ])
      trackEvent('evaluation_requested', { startup_id: startupId })
    },
  })
}

export function useAutoUnlockTopMatchMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AutoUnlockTopMatchMutation(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: v2ListStartupMatchesQueryKey({ path: { startupId } }),
        }),
        queryClient.invalidateQueries({
          queryKey: activeEntitlementsQueryKey(startupId),
        }),
      ])
    },
  })
}

export function useFinishPitchMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2FinishPitchMutation(),
    // Invalidate on settle, not on success: a failed finish must still drop the
    // cached IN_PROGRESS session, or the pitch route re-mounts the room with a
    // still-valid LiveKit token.
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: v2GetStartupPitchQueryKey({} as never) })
    },
  })
}

export function useGetDocumentDownloadUrlMutation(callbacks?: {
  onSuccess?: (data: GetDocumentDownloadUrlResponse) => void
  onError?: (error: Error) => void
}) {
  return useMutation({
    ...getDocumentDownloadUrlMutation(),
    onSuccess: (data) => callbacks?.onSuccess?.(data),
    onError: (error) => callbacks?.onError?.(error),
  })
}

export function useFeedQuery(startupId: string) {
  return useQuery(v2GetStartupFeedOptions({ path: { startupId } }))
}

export function useInvestorFeedQuery(investorId: string) {
  return useQuery({
    ...v2GetInvestorFeedOptions({ path: { investorId } }),
    refetchInterval: (query) => {
      const items = query.state.data?.data
      const hasActive = items?.some(
        (item) => item.prescreenStatus === 'PENDING' || item.prescreenStatus === 'PROCESSING'
      )
      return hasActive ? 5000 : false
    },
  })
}

export function useInvestorConnectMutation() {
  return useMutation(v2InvestorConnectWithStartupMutation())
}

export function useInvestorEvaluationQuery(investorId: string, evaluationId: string | undefined) {
  return useQuery({
    ...v2GetInvestorEvaluationOptions({
      path: { investorId, evaluationId: evaluationId ?? '' },
    }),
    enabled: !!evaluationId,
  })
}

export function useInvestorJudgeProfileQuery(
  investorId: string,
  evaluationId: string | undefined,
  judgeKey: string | undefined
) {
  return useQuery({
    ...v2GetInvestorJudgeProfileOptions({
      path: {
        investorId,
        evaluationId: evaluationId ?? '',
        judgeKey: judgeKey ?? '',
      },
    }),
    enabled: !!evaluationId && !!judgeKey,
  })
}

export function usePitchEvaluationQuery(startupId: string, evaluationId: string | undefined) {
  return useQuery({
    ...v2GetPitchEvaluationOptions({
      path: { startupId, evaluationId: evaluationId ?? '' },
    }),
    enabled: !!evaluationId,
  })
}

export function usePitchVideoUrlMutation() {
  return useMutation(v2GetPitchVideoUrlMutation())
}

export function usePublicVideoUrlMutation() {
  return useMutation(v2PublicVideoUrlMutation())
}

// ─── Credits ──────────────────────────────────────────────────────────────────

export function useStartupCreditBalanceQuery(startupId: string | undefined) {
  return useQuery({
    ...v2GetStartupCreditBalanceOptions({ path: { startupId: startupId ?? '' } }),
    select: (response) => response.data?.balance,
    enabled: !!startupId,
  })
}

export function useStartupCreditsQuery(
  startupId: string | undefined,
  queryOptions?: Partial<ReturnType<typeof v2ListStartupCreditsOptions>>
) {
  return useQuery({
    ...v2ListStartupCreditsOptions({ path: { startupId: startupId ?? '' } }),
    ...queryOptions,
    select: (response: V2ListStartupCreditsResponse) => response.data,
    enabled: !!startupId && (queryOptions?.enabled ?? true),
  })
}

export function useInvestorCreditsQuery(investorId: string | undefined) {
  return useQuery({
    ...v2ListInvestorCreditsOptions({ path: { investorId: investorId ?? '' } }),
    select: (response: V2ListInvestorCreditsResponse) => response.data,
    enabled: !!investorId,
  })
}

// ─── Billing (pay-as-you-go) ─────────────────────────────────────────────────

export function useBillingStatusQuery(startupId: string | undefined) {
  return useQuery({
    ...v2GetBillingStatusOptions({ query: { startupId: startupId ?? '' } }),
    enabled: !!startupId,
  })
}

/**
 * Unified wallet balance for a startup. One balance powers outreach,
 * fund evaluations, and AI pitch sessions.
 */
export function useStartupWalletQuery(startupId: string | undefined) {
  return useQuery({
    ...v2GetStartupWalletOptions({ path: { id: startupId ?? '' } }),
    enabled: !!startupId,
  })
}

/**
 * Start a Stripe Checkout to top up the wallet. Returns a checkout URL; the
 * caller redirects via `window.location.assign`.
 */
export function useWalletTopUpMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2CreateWalletTopUpCheckoutMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: v2GetStartupWalletQueryKey({ path: { id: startupId } }),
      })
    },
  })
}

/**
 * Open the Stripe billing portal to manage the saved card. Returns a portal
 * URL; the caller redirects via `window.location.assign`.
 */
export function useWalletBillingPortalMutation() {
  return useMutation(v2OpenWalletBillingPortalMutation())
}

/**
 * Active entitlements for a startup. Read by the studio to decide
 * locked/unlocked state. Decoupled from the subscription status response
 * so the entitlement subsystem stays independent of billing.
 */
export function useActiveEntitlementsQuery(startupId: string | undefined) {
  return useQuery({
    ...v2GetActiveEntitlementsOptions({ path: { startupId: startupId ?? '' } }),
    enabled: !!startupId,
  })
}

export function activeEntitlementsQueryKey(startupId: string) {
  return v2GetActiveEntitlementsQueryKey({ path: { startupId } })
}

// ─── Evaluation ───────────────────────────────────────────────────────────────

export function useJudgeProfileQuery(
  startupId: string,
  evaluationId: string | undefined,
  judgeKey: string
) {
  return useQuery({
    ...v2GetJudgeProfileOptions({
      path: { startupId, evaluationId: evaluationId ?? '', judgeKey },
    }),
    enabled: !!evaluationId,
  })
}

// ─── Startup profile (v2 real data) ──────────────────────────────────────────

export function useStartupProfileQuery(startupId: string) {
  return useQuery({
    ...v2GetStartupProfileOptions({ path: { startupId } }),
    select: (response: V2GetStartupProfileResponse) => response.data,
    // Poll while the workspace is still assembling — this payload is what
    // unlocks the nav, so nothing else can notice that it finished.
    //
    // Two phases, and the second one used to be missed: the deck flips to READY
    // when its text is extracted, but the evaluation behind Feedback lands
    // minutes later. Stopping at READY left Feedback locked until the founder
    // reloaded, right after signing up. FAILED and NONE stop the poll — there
    // is nothing further coming.
    refetchInterval: (query) => {
      const data = (query.state.data as V2GetStartupProfileResponse | undefined)?.data
      if (!data) return false
      if (data.pitchDeckStatus === 'PROCESSING') return 5_000
      if (data.pitchDeckStatus === 'READY' && !data.hasEvaluation) return 10_000
      return false
    },
  })
}

export function useUpdateStartupMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UpdateStartupProfileMutation({ path: { startupId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const id = (query.queryKey[0] as { _id?: string })?._id
          return (
            id === 'v2GetStartupProfile' ||
            id === 'v2GetPublicProfileFull' ||
            id === 'v2GetStartupOverview'
          )
        },
      })
    },
  })
}

export function useSetStealthChoiceMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2SetStartupStealthChoiceMutation({ path: { startupId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2GetStartupProfileQueryKey({ path: { startupId } }),
      })
    },
  })
}

/** Stamps the once-per-day cap on the post-improvement prompt, server-side
 *  so it holds across the founder's browsers/devices. */
export function useMarkPostImprovementPromptShownMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2MarkPostImprovementPromptShownMutation({ path: { startupId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2GetStartupProfileQueryKey({ path: { startupId } }),
      })
    },
  })
}

// ─── Startup members (v1 — no v2 equivalent) ────────────────────────────────

export function useStartupMembersQuery(startupId: string | undefined) {
  return useQuery({
    ...v2ListAccessOptions({
      path: { startupId: startupId ?? '' },
    }),
    select: (response: V2ListAccessResponse) => response.data,
    enabled: !!startupId,
  })
}

export function useRemoveStartupMemberMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2RemoveAccessMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2ListAccessQueryKey({ path: { startupId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2GetStartupProfileQueryKey({ path: { startupId } }),
      })
    },
  })
}

// ─── Team members (v2) ───────────────────────────────────────────────────────

export function useTeamMembersQuery(startupId: string) {
  return useQuery({
    ...v2ListTeamMembersOptions({ path: { startupId } }),
    select: (response: V2ListTeamMembersResponse) => response.data,
    refetchInterval: (query) => {
      const response = query.state.data as V2ListTeamMembersResponse | undefined
      const anyScraping = response?.data.some((m) => m.linkedinScrapeStatus === 'SCRAPING')
      return anyScraping ? 3000 : false
    },
  })
}

function invalidateTeamQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const id = (query.queryKey[0] as { _id?: string })?._id
      return (
        id === 'v2ListTeamMembers' ||
        id === 'v2GetStartupProfile' ||
        id === 'v2GetPublicProfileFull'
      )
    },
  })
}

export function useCreateTeamMemberMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2CreateTeamMemberMutation({ path: { startupId } }),
    onSuccess: () => invalidateTeamQueries(queryClient),
  })
}

export function useUpdateTeamMemberMutation(_startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UpdateTeamMemberMutation(),
    onSuccess: () => invalidateTeamQueries(queryClient),
  })
}

export function useAdminBackfillTeamMemberLinkedInMutation() {
  return useMutation({
    ...v2AdminBackfillTeamMemberLinkedInMutation(),
  })
}

export function useDeleteTeamMemberMutation(_startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2DeleteTeamMemberMutation(),
    onSuccess: () => invalidateTeamQueries(queryClient),
  })
}

export function useRefreshTeamMemberLinkedInMutation(_startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2RefreshTeamMemberLinkedInMutation(),
    onSuccess: () => invalidateTeamQueries(queryClient),
  })
}

// ─── Invites (v2) ────────────────────────────────────────────────────────────

export function useStartupInvitesQuery(startupId: string | undefined) {
  return useQuery({
    ...v2ListStartupInvitesOptions({
      path: { startupId: startupId ?? '' },
    }),
    select: (response: V2ListStartupInvitesResponse) => response.data,
    enabled: !!startupId,
  })
}

export function useCreateInviteMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2CreateStartupInviteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2ListStartupInvitesQueryKey({ path: { startupId } }),
      })
    },
  })
}

export function useCancelInviteMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2CancelStartupInviteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2ListStartupInvitesQueryKey({ path: { startupId } }),
      })
    },
  })
}

// ─── Analytics settings (exclusions) ────────────────────────────────────────

export function useAnalyticsSettingsQuery(startupId: string | undefined) {
  return useQuery({
    ...v2GetAnalyticsSettingsOptions({ path: { startupId: startupId ?? '' } }),
    enabled: !!startupId,
    select: (response) => response.data,
  })
}

export function useCreateAnalyticsExclusionMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2CreateAnalyticsExclusionMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2GetAnalyticsSettingsQueryKey({ path: { startupId } }),
      })
    },
  })
}

export function useDeleteAnalyticsExclusionMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2DeleteAnalyticsExclusionMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2GetAnalyticsSettingsQueryKey({ path: { startupId } }),
      })
    },
  })
}

export function useAcceptInviteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AcceptInviteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2GetMyMembershipsQueryKey() })
    },
  })
}

// ─── Investor invites (v2) ───────────────────────────────────────────────────

export function useInvestorInvitesQuery(investorId: string | undefined) {
  return useQuery({
    ...v2ListInvestorInvitesOptions({
      path: { investorId: investorId ?? '' },
    }),
    select: (response: V2ListInvestorInvitesResponse) => response.data,
    enabled: !!investorId,
  })
}

export function useCreateInvestorInviteMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2CreateInvestorInviteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2ListInvestorInvitesQueryKey({ path: { investorId } }),
      })
    },
  })
}

export function useCancelInvestorInviteMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2CancelInvestorInviteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2ListInvestorInvitesQueryKey({ path: { investorId } }),
      })
    },
  })
}

// ─── Startup config ─────────────────────────────────────────────────────────

export function useStartupConfigQuery(
  queryOptions?: Partial<ReturnType<typeof getStartupConfigOptions>>
) {
  return useQuery({
    ...getStartupConfigOptions(),
    ...queryOptions,
    select: (response: GetStartupConfigResponse) => response.data,
  })
}

// ─── Investor config ────────────────────────────────────────────────────────

export function useInvestorConfigQuery(
  queryOptions?: Partial<ReturnType<typeof getInvestorConfigOptions>>
) {
  return useQuery({
    ...getInvestorConfigOptions(),
    ...queryOptions,
    select: (response: GetInvestorConfigResponse) => response.data,
  })
}

export function useInvestorQuery() {
  return useQuery({
    ...getInvestorOptions(),
    select: (response: GetInvestorResponse) => response.data,
  })
}

export function usePublicInvestorQuery(slug: string) {
  return useQuery({
    ...v2GetPublicInvestorBySlugOptions({ path: { slug } }),
    select: (response: V2GetPublicInvestorBySlugResponse) => response.data,
  })
}

export function useInvestorCreditBalanceQuery(investorId: string | undefined) {
  return useQuery({
    ...v2GetInvestorCreditBalanceOptions({ path: { investorId: investorId ?? '' } }),
    select: (response) => response.data?.balance,
    enabled: !!investorId,
  })
}

export function useInvestorProfileQuery(
  investorId: string,
  queryOptions?: Partial<ReturnType<typeof v2GetInvestorProfileOptions>>
) {
  return useQuery({
    ...v2GetInvestorProfileOptions({ path: { investorId } }),
    ...queryOptions,
    select: (response: V2GetInvestorProfileResponse) => response.data,
  })
}

export function useInvestorPersonaQuery(investorId: string) {
  return useQuery({
    ...v2GetInvestorPersonaOptions({ path: { investorId } }),
    select: (response: V2GetInvestorPersonaResponse) => response.data,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.personaStatus
      return status === 'processing' ? 5000 : false
    },
  })
}

export function useUpdateInvestorMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UpdateInvestorProfileMutation({ path: { investorId } }),
    // On settle, not on success: the profile is saved before the persona rebuild starts, so a rebuild
    // that fails still leaves a saved change on the server that the page must show.
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: v2GetInvestorProfileQueryKey({ path: { investorId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2GetInvestorPersonaQueryKey({ path: { investorId } }),
      })
    },
  })
}

/** The signed-in person's own title at the investor; founders see it when they follow. */
export function useUpdateInvestorMemberMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UpdateInvestorMemberMutation({ path: { investorId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2GetInvestorProfileQueryKey({ path: { investorId } }),
      })
    },
  })
}

// ─── Documents ──────────────────────────────────────────────────────────────

export type StartupDocument = NonNullable<V2ListStartupDocumentsResponse['data']>[number]

export function useStartupDocumentsQuery(startupId: string | undefined) {
  return useQuery({
    ...v2ListStartupDocumentsOptions({
      path: { startupId: startupId ?? '' },
    }),
    select: (response: V2ListStartupDocumentsResponse) => response.data,
    enabled: !!startupId,
  })
}

export function useCreateDocumentMutation() {
  return useMutation({
    ...createDocumentMutation(),
  })
}

export function useVerifyDocumentMutation() {
  return useMutation({
    ...verifyDocumentMutation(),
  })
}

export function useDeleteDocumentMutation() {
  return useMutation({
    ...deleteDocumentMutation(),
  })
}

export function useLinkStartupDocumentMutation(_startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2LinkStartupDocumentMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const id = (query.queryKey[0] as { _id?: string })?._id
          // Linking a pitch deck changes dashboardStatus (no_deck → processing)
          // and kicks off the prescreen server-side, so refresh the overview too.
          return (
            id === 'v2ListStartupDocuments' ||
            id === 'v2GetPublicProfileFull' ||
            id === 'v2GetStartupOverview'
          )
        },
      })
    },
  })
}

export function useUnlinkStartupDocumentMutation(_startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UnlinkStartupDocumentMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const id = (query.queryKey[0] as { _id?: string })?._id
          return id === 'v2ListStartupDocuments' || id === 'v2GetPublicProfileFull'
        },
      })
    },
  })
}

export function useUpdateStartupDocumentMutation(_startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UpdateStartupDocumentMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => {
          const id = (query.queryKey[0] as { _id?: string })?._id
          return id === 'v2ListStartupDocuments' || id === 'v2GetPublicProfileFull'
        },
      })
    },
  })
}

/**
 * Upload a file to S3 via presigned URL, verify it, and return the document data.
 * Flow: POST /documents → upload to S3 → PATCH /documents/:id (verify)
 */
export async function uploadDocument(
  file: File,
  createDoc: ReturnType<typeof useCreateDocumentMutation>,
  verifyDoc: ReturnType<typeof useVerifyDocumentMutation>
): Promise<{ documentId: string; fileName: string; fileSize: number }> {
  // 1. Create document record and get presigned upload URL
  const createResult = (await createDoc.mutateAsync({
    body: { fileName: file.name },
  })) as Extract<CreateDocumentResponse, { status: 'ok' }>

  const { id, uploadUrl, uploadFields } = createResult.data

  // 2. Upload file directly to S3 via presigned POST
  const formData = new FormData()
  for (const [key, value] of Object.entries(uploadFields)) {
    formData.append(key, value)
  }
  formData.append('file', file)
  const uploaded = await fetch(uploadUrl, { method: 'POST', body: formData })
  if (!uploaded.ok) throw new Error(`Upload to storage failed (${uploaded.status})`)

  // 3. Verify upload
  await verifyDoc.mutateAsync({ path: { id } })

  return { documentId: id, fileName: file.name, fileSize: file.size }
}

// ─── Investor Evaluation ────────────────────────────────────────────────────

export function useRequestInvestorEvaluationMutation() {
  return useMutation({
    ...v2RequestInvestorEvaluationMutation(),
  })
}

export function useEvaluateStartupMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2EvaluateStartupMutation(),
    onSuccess: (_data, variables) => {
      const investorId = variables.path?.investorId
      if (!investorId) return
      queryClient.invalidateQueries({
        queryKey: v2ListDealFlowMatchesQueryKey({ path: { investorId } }),
      })
    },
  })
}

export function useInvestorEvaluationRequestsQuery(investorId: string) {
  return useQuery(v2ListInvestorEvaluationRequestsOptions({ path: { investorId } }))
}

// ─── Prescreen ──────────────────────────────────────────────────────────────

export function usePrescreenAllowanceQuery(startupId: string) {
  return useQuery({
    ...v2GetPrescreenAllowanceOptions({ path: { startupId } }),
    select: (response: V2GetPrescreenAllowanceResponse) => response.data,
    enabled: !!startupId,
    // Poll while a run is active so the button re-enables when it finishes.
    refetchInterval: (query) => (query.state.data?.data.isRunning ? 15_000 : false),
  })
}

export function useTriggerPrescreenMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2TriggerPrescreenMutation(),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: v2GetStartupOverviewQueryKey({ path: { startupId } }),
      })
      queryClient.invalidateQueries({
        queryKey: v2GetPrescreenAllowanceQueryKey({ path: { startupId } }),
      })
    },
  })
}

export function useCreateFeedbackMutation() {
  return useMutation({
    ...v2CreateFeedbackMutation(),
  })
}

export function useGenerateContactMessageMutation() {
  return useMutation({
    ...v2GenerateInvestorContactMessageMutation(),
  })
}

export function useStartupOutreachBodyQuery(startupId: string) {
  return useQuery({
    ...v2GetStartupOutreachBodyOptions({ path: { startupId } }),
  })
}

export function useUpdateStartupOutreachBodyMutation(startupId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UpdateStartupOutreachBodyMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: v2GetStartupOutreachBodyQueryKey({ path: { startupId } }),
      })
    },
  })
}

// ─── Deal flow subscription ─────────────────────────────────────────────────

export function useDealFlowSubscriptionStatusQuery(investorId: string | undefined) {
  return useQuery({
    ...v2GetDealFlowSubscriptionStatusOptions({ path: { investorId: investorId ?? '' } }),
    enabled: !!investorId,
  })
}

export function useSubscribeToDealFlowMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2SubscribeToDealFlowMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2GetDealFlowSubscriptionStatusQueryKey({ path: { investorId } }),
      })
      // Backend kicks off engine refresh; matches will appear in DB shortly.
      queryClient.invalidateQueries({
        queryKey: v2ListDealFlowMatchesQueryKey({ path: { investorId } }),
      })
    },
  })
}

export function useUnsubscribeFromDealFlowMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2UnsubscribeFromDealFlowMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2GetDealFlowSubscriptionStatusQueryKey({ path: { investorId } }),
      })
    },
  })
}

export function useDealFlowMatchesQuery(investorId: string | undefined) {
  return useQuery({
    ...v2ListDealFlowMatchesOptions({ path: { investorId: investorId ?? '' } }),
    enabled: !!investorId,
    refetchInterval: (query) => {
      const matches = query.state.data?.data ?? []
      const hasPending = matches.some(
        (m) =>
          m.customEvaluation?.status === 'PENDING_PERSONA' ||
          m.customEvaluation?.status === 'PENDING_EVALUATION'
      )
      return hasPending ? 10_000 : false
    },
  })
}

export function useDealFlowArchiveQuery(investorId: string | undefined) {
  return useQuery({
    ...v2ListDealFlowArchiveOptions({ path: { investorId: investorId ?? '' } }),
    enabled: !!investorId,
  })
}

export function useDealFlowMatchQuery(
  investorId: string | undefined,
  startupId: string | undefined
) {
  return useQuery({
    ...v2GetDealFlowMatchOptions({
      path: { investorId: investorId ?? '', startupId: startupId ?? '' },
    }),
    enabled: !!investorId && !!startupId,
  })
}

export function useDealFlowMatchDocumentDownloadMutation() {
  return useMutation(v2DownloadDealFlowMatchDocumentMutation())
}

export function useDealFlowMatchVideoUrlMutation() {
  return useMutation(v2GetDealFlowMatchVideoUrlMutation())
}

export function usePassDealFlowMatchMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2PassDealFlowMatchMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2ListDealFlowMatchesQueryKey({ path: { investorId } }),
      })
    },
  })
}

export function useConnectDealFlowMatchMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2ConnectDealFlowMatchMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: v2ListDealFlowMatchesQueryKey({ path: { investorId } }),
      })
    },
  })
}

export function useAdminInvestorDealFlowInteractionsQuery(investorId: string | undefined) {
  return useQuery({
    ...v2AdminListInvestorDealFlowInteractionsOptions({ path: { id: investorId ?? '' } }),
    enabled: !!investorId,
  })
}

export function useAdminStartupDealFlowInteractionsQuery(startupId: string | undefined) {
  return useQuery({
    ...v2AdminListStartupDealFlowInteractionsOptions({ path: { id: startupId ?? '' } }),
    enabled: !!startupId,
  })
}

export function useAdminInvestorDealFlowMatchesQuery(investorId: string | undefined) {
  return useQuery({
    ...v2AdminListInvestorDealFlowMatchesOptions({ path: { id: investorId ?? '' } }),
    enabled: !!investorId,
    refetchInterval: (q) => {
      const status = q.state.data?.data?.latestRefreshRequest?.status
      return status === 'PENDING' || status === 'PROCESSING' ? 3000 : false
    },
  })
}

export function useAdminRefreshInvestorDealFlowMatchesMutation(investorId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    ...v2AdminRefreshInvestorDealFlowMatchesMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: v2AdminListInvestorDealFlowMatchesQueryKey({ path: { id: investorId } }),
      })
    },
  })
}

export function useAdminSendInvestorDealFlowDigestMutation() {
  return useMutation({
    ...v2AdminSendInvestorDealFlowDigestMutation(),
  })
}

// ─── Investor preference normalization ───────────────────────────────────────
// Direct client.post fallback — codegen blocked by pre-existing
// @workspace/api-engine import error in routes-v2/admin.ts.

export interface AdminUnknownPreferenceValue {
  value: string
  investorCount: number
}

export interface AdminListUnknownInvestorPreferencesData {
  areas: AdminUnknownPreferenceValue[]
  regions: AdminUnknownPreferenceValue[]
}

export interface AdminNormalizeInvestorPreferencesPayload {
  areaDictionary: Record<string, string>
  regionDictionary: Record<string, string>
  dryRun: boolean
}

export interface AdminNormalizeInvestorChange {
  id: string
  name: string
  beforeAreas: string[]
  afterAreas: string[]
  beforeRegions: string[]
  afterRegions: string[]
}

export interface AdminNormalizeInvestorPreferencesData {
  dryRun: boolean
  totalInvestors: number
  changed: number
  unchanged: number
  cleared: number
  droppedAreaValues: string[]
  droppedRegionValues: string[]
  changes: AdminNormalizeInvestorChange[]
}

// hey-api types `response.data` as the inner field of the generic, but
// the runtime body is the full envelope `{ status: 'ok', data: T }`. We
// cast through `unknown` and unwrap manually so callers get the actual
// payload.
interface ApiEnvelope<T> {
  status: 'ok'
  data: T
}

export function useAdminUnknownInvestorPreferencesQuery() {
  return useQuery({
    queryKey: ['admin', 'investors', 'unknown-preferences'],
    queryFn: async () => {
      const response = await client.get<{ data: AdminListUnknownInvestorPreferencesData }>({
        url: '/api/v2/admin/investors/unknown-preferences',
      })
      if (response.error) throw response.error
      const envelope = response.data as unknown as
        | ApiEnvelope<AdminListUnknownInvestorPreferencesData>
        | undefined
      if (!envelope?.data) throw new Error('Empty response')
      return envelope.data
    },
  })
}

export function useAdminNormalizeInvestorPreferencesMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: AdminNormalizeInvestorPreferencesPayload) => {
      const response = await client.post<{ data: AdminNormalizeInvestorPreferencesData }>({
        url: '/api/v2/admin/investors/normalize-preferences',
        body: payload,
      })
      if (response.error) throw response.error
      const envelope = response.data as unknown as
        | ApiEnvelope<AdminNormalizeInvestorPreferencesData>
        | undefined
      if (!envelope?.data) throw new Error('Empty response')
      return envelope.data
    },
    onSuccess: async (data) => {
      if (!data.dryRun) {
        await queryClient.invalidateQueries({
          queryKey: ['admin', 'investors', 'unknown-preferences'],
        })
      }
    },
  })
}

// ─── Ask the profile (public share link chat) ──────────────────────────────

/**
 * The context call is a POST that carries `visitorToken` in the body — a
 * capability token in a query string lands in access logs, proxy logs and any
 * `Referer` — so it has no generated query helper, only a mutation one. It is
 * a read, so it is wrapped as a query over the generated SDK function rather
 * than fired as a mutation.
 */
export function useProfileChatContextQuery(token: string, visitorToken: string) {
  return useQuery({
    queryKey: ['public-profile', token, 'chat-context', visitorToken],
    queryFn: async () => {
      const response = await v2ProfileChatContext({ path: { token }, body: { visitorToken } })
      if (response.error) throw response.error
      if (!response.data) throw new Error('Empty response')
      return response.data.data
    },
    // The quota is authoritative on every `done` frame, so refetching on a
    // window focus would only overwrite a fresher number with a staler one.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  })
}

/**
 * A reload's own read: prior turns of the chat session on screen. A plain
 * async function rather than a `useQuery`, because it only ever runs once,
 * imperatively, from the controller's mount effect — there is no component
 * that wants to declaratively subscribe to "the history of this session".
 */
export async function fetchProfileChatHistory(
  token: string,
  body: { visitorToken: string; chatSessionId: string }
) {
  const response = await v2ProfileChatHistory({ path: { token }, body })
  if (response.error) throw response.error
  if (!response.data) throw new Error('Empty response')
  return response.data.data.turns
}

/** The generated request body, so the two sides cannot drift apart. */
export type ProfileChatRequest = V2ProfileChatData['body']

/**
 * A failed chat request, carrying the server's machine `code` when it sent one
 * (`daily_limit` is the one the composer reads). Thrown before any frame.
 */
export class ProfileChatHttpError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null
  ) {
    super(`Chat request failed with status ${status}`)
  }
}

async function throwIfChatFailed(response: Response): Promise<void> {
  if (response.ok) return
  const body: unknown = await response.json().catch(() => null)
  const code =
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error !== null &&
    'code' in body.error &&
    typeof body.error.code === 'string'
      ? body.error.code
      : null
  throw new ProfileChatHttpError(response.status, code)
}

/**
 * Open the answer stream.
 *
 * Raw `fetch`, bypassing the generated client, because that client consumes the
 * whole body before it returns and this one is read incrementally. A non-2xx is
 * thrown before a single frame is parsed, so the pane can offer a retry rather
 * than half an answer.
 */
export async function openProfileChatStream(
  token: string,
  body: ProfileChatRequest & { visitorToken: string },
  signal: AbortSignal
): Promise<Response> {
  const response = await fetch(
    `${import.meta.env.VITE_BACKEND_URL}/api/v2/public/profile/${token}/chat`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    }
  )
  await throwIfChatFailed(response)
  return response
}

/**
 * The founder's own test drive, on their own profile.
 *
 * A different route from the public one, not a flag on it: this is
 * authenticated, so it carries the session cookie instead of a visitor token,
 * and the server writes no turn row and no events for it. That is what keeps a
 * founder's own reading out of their analytics.
 */
export async function openProfileChatPreviewStream(
  startupId: string,
  body: {
    presetKey?: string
    slideNumber?: number
    question?: string
    isFollowUp?: boolean
  },
  signal: AbortSignal
): Promise<Response> {
  const response = await fetch(
    `${import.meta.env.VITE_BACKEND_URL}/api/v2/startups/${startupId}/chat/preview`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
      signal,
    }
  )
  await throwIfChatFailed(response)
  return response
}

/**
 * The reading checklist for the founder preview.
 *
 * A POST like its public sibling, so it has no generated query helper and is
 * wrapped by hand — it is a read, so it is a query rather than a mutation.
 */
export function useProfileChatPreviewContextQuery(startupId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['startup', startupId, 'chat-preview-context'],
    queryFn: async () => {
      const response = await v2ProfileChatPreviewContext({ path: { startupId } })
      if (response.error) throw response.error
      if (!response.data) throw new Error('Empty response')
      return response.data.data
    },
    enabled: enabled && startupId !== '',
    // Deliberately NOT cached forever, unlike its public sibling: this endpoint
    // 404s the moment the feature flag or the founder's own switch goes off,
    // and an infinite staleTime meant a founder kept seeing the widget after
    // turning it off until they hard-reloaded.
    retry: false,
  })
}
